import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { MdAdd, MdDelete, MdOutlineSearch } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type {
  CellContextMenuEvent,
  GetContextMenuItemsParams,
  GetRowIdParams,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideSelectionState,
  MenuItemDef,
  RowClickedEvent,
  RowNode,
  SelectionChangedEvent,
} from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, RefObject, SyntheticEvent } from 'react'
import type { IContext } from '~/components/CellRenderers/ClusterApplicationCellRenderer'
import type { IClusterApp, IListAppsForClusterPayload } from '~/types/cluster'

import columns, { COLUMNS } from './columns'

import ClusterFilters from '../Filters'

import { listAppsForCluster } from '~/api/clusters'
import Button from '~/components/Buttons/Button'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import { useClustersContext } from '~/providers/Clusters'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { keys as clusterKeys } from '~/queries/clusters'
import { debounce } from '~/utils/function'

const CACHE_BLOCK_SIZE = 50

const ENTRIES_NAME = 'competitors-cluster-details-apps'

interface Props {
  clusterId: string
  clusterTags: string[]
  getContextMenuItems: (params: GetContextMenuItemsParams) => MenuItemDef[]
  gridRef: RefObject<AgGridReact<IClusterApp>> | null
  onAddAppToCluster: VoidFunction
  onContactIconClick: (email: string, phone: string) => void
  onDeleteAppFromCluster: VoidFunction
  onDeleteTag: (value: { deviceType: string; storeId: string; tags: string[] }, gridApi: GridApi) => Promise<void>
  onDisplayScreenshots: (app: IClusterApp) => void
  onRowSelectionChanged: (selectedRows: IClusterApp[]) => void
}

const AppsListingTable = ({
  clusterId,
  clusterTags,
  getContextMenuItems,
  gridRef,
  onAddAppToCluster,
  onContactIconClick,
  onDeleteAppFromCluster,
  onDeleteTag,
  onDisplayScreenshots,
  onRowSelectionChanged,
}: Props): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const { dispatch: dispatchToClustersContext, state } = useClustersContext()

  const { dispatchToSearchParams } = useSearchParamsContext()

  const tableRef = useRef<HTMLDivElement>(null)

  const [selectedRows, setSelectedRows] = useState<IClusterApp[]>([])

  const [search, setSearch] = useState('')

  const debouncedSearch = debounce((search) => setSearch(search), 500)

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        const { api, fail, request, success } = params

        api.hideOverlay()

        const currentPage = (request.startRow || 0) / CACHE_BLOCK_SIZE

        const opFiltering = request.filterModel
          ? Object.entries(request.filterModel)
              .map(
                ([key, { filter, filterTo, type }]) =>
                  `${type}(${key}, ${type === 'inRange' ? `${filter}, ${filterTo}` : `${filter}`})`
              )
              .join('+')
          : undefined

        const queryParams: IListAppsForClusterPayload = {
          clusterId,
          deviceType: state.deviceType,
          filterPreset: state.filterPreset,
          opFiltering,
          orderBy: request.sortModel.length ? `${request.sortModel[0].colId}+${request.sortModel[0].sort}` : undefined,
          page: currentPage,
          q: search,
          size: CACHE_BLOCK_SIZE,
          tags: state.tags,
        }

        const { data, total_filtered } = await queryClient.fetchQuery({
          queryFn: () => listAppsForCluster(queryParams),
          queryKey: clusterKeys.listClusters(queryParams),
        })

        dispatchToClustersContext({ type: 'nbVisibleApps', value: total_filtered })

        if (data) {
          if (!data.length) {
            // NOTE: dispatchToClustersContext is re-rendering the view hidding the overlay,
            // setTimeout is a workaround to display the overlay after the state update
            setTimeout(() => api.showNoRowsOverlay(), 50)
          }
          success({ rowData: data })
        } else {
          fail()
        }
      },
    }),
    [clusterId, dispatchToClustersContext, queryClient, search, state.deviceType, state.filterPreset, state.tags]
  )

  /** Handlers */

  const handleGetRowId = useCallback(({ data }: GetRowIdParams<IClusterApp>) => data.store_id, [])

  const handleRowClicked = useCallback(
    ({ node }: RowClickedEvent<IClusterApp>) => {
      const colId = gridRef?.current?.api.getFocusedCell()?.column.getColId()
      const actionsColIds = ['selection_checkbox', COLUMNS.APPLICATION, COLUMNS.SCREENSHOTS]

      if (colId && !actionsColIds.includes(colId) && node.data) {
        navigate(
          `/competitors/clusters/${clusterId}/app-infos/${node.data.store_id}/device-type/${node.data.device_type}`
        )
      } else if (colId === COLUMNS.SCREENSHOTS && node.data) {
        onDisplayScreenshots(node.data)
      }
    },
    [clusterId, gridRef, navigate, onDisplayScreenshots]
  )

  const handleRowSelectionChanged = useCallback(
    (evt: SelectionChangedEvent<IClusterApp>) => {
      const state = evt.api.getServerSideSelectionState() as IServerSideSelectionState | null
      const newSelectedRows: IClusterApp[] = []

      if (state && !state.selectAll && state.toggledNodes?.length) {
        evt.api.forEachNode(
          (node) => node.data && state.toggledNodes.includes(node.data.store_id) && newSelectedRows.push(node.data)
        )
      } else if (state && state.selectAll) {
        evt.api.forEachNode((node) => node.data && newSelectedRows.push(node.data))
      }

      setSelectedRows(newSelectedRows)
      onRowSelectionChanged(newSelectedRows)
    },
    [onRowSelectionChanged]
  )

  const handleSearchChange = (e: SyntheticEvent<HTMLInputElement>) => debouncedSearch(e.currentTarget.value)

  const handleSearchSimilarApps = () => {
    const selectedStoreIds = selectedRows
      .filter(({ store_id }) => store_id !== null)
      .map(({ store_id }) => store_id) as string[]

    dispatchToClustersContext({ type: 'listingMode', value: 'candidates' })
    dispatchToClustersContext({ type: 'searchElements', value: selectedStoreIds })
    dispatchToSearchParams({ type: 'update', value: { listingMode: 'candidates' } })
    dispatchToSearchParams({ type: 'update', value: { 'searchElements[]': selectedStoreIds } })
  }

  const handleCellContextMenu = async ({ api, event, node }: CellContextMenuEvent) => {
    const castedNode = node as RowNode
    const castedEvent = event as PointerEvent

    if (castedEvent) {
      api.showContextMenu({ rowNode: castedNode, value: getContextMenuItems, x: castedEvent.x, y: castedEvent.y })
    }
  }

  const handleDeleteTag = (params: { deviceType: string; storeId: string; tags: string[] }) =>
    gridRef?.current?.api && onDeleteTag(params, gridRef.current.api)

  /** Render */

  const context: IContext = {
    onContactIconClick,
    onDeleteTag: handleDeleteTag,
  }

  return (
    <ServerSideTableWrapper
      buttonComponent={
        <div className="cluster-details-view__table__actions">
          <Button disabled={selectedRows.length < 1} onClick={handleSearchSimilarApps} size="small">
            <MdOutlineSearch color="#fff" size={16} />
            Search similar apps
          </Button>
          <Button disabled={selectedRows.length < 1} onClick={onDeleteAppFromCluster} outlined size="small">
            <MdDelete color="#ff5301" size={16} />
          </Button>
          <Button onClick={onAddAppToCluster} size="small">
            <MdAdd color="#fff" size={16} />
            Add an app
          </Button>
        </div>
      }
      cacheBlockSize={CACHE_BLOCK_SIZE}
      className="cluster-details-view__table cluster-details-view__apps-table"
      columnDefs={columns}
      context={context}
      defaultColDef={{
        enableCellChangeFlash: true,
        filterParams: { buttons: ['clear'], debounceMs: 500 },
        getQuickFilterText: () => '',
        menuTabs: ['filterMenuTab'],
      }}
      entriesName={ENTRIES_NAME}
      getContextMenuItems={getContextMenuItems}
      getRowId={handleGetRowId}
      isSideBar
      nodeBeforeSearchBar={<ClusterFilters clusterTags={clusterTags} />}
      onCellContextMenu={handleCellContextMenu}
      onRowClicked={handleRowClicked}
      onSearchChange={handleSearchChange}
      onSelectionChanged={handleRowSelectionChanged}
      popupParent={document.querySelector('body')}
      preventDefaultOnContextMenu
      ref={gridRef}
      rowHeight={250}
      rowModelType="serverSide"
      rowSelection={{
        enableClickSelection: true,
        headerCheckbox: false,
        mode: 'multiRow',
      }}
      serverSideDatasource={datasource}
      suppressCsvExport
      suppressExcelExport
      tableRef={tableRef}
      withPagination={false}
      withSearchBar
    />
  )
}

export default AppsListingTable
